import { TweenLite } from 'gsap';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components/macro';

type Props = {
  isExpanded: boolean;
  animationDurationSeconds?: number;
  children?: React.ReactNode;
  className?: string;
  // When automatically copying data to clipboard from within an expandable section, adding `display:none` prevents this from working
  disableDisplayNone?: boolean;
};

export const Expandable = ({
  isExpanded,
  animationDurationSeconds,
  children,
  className,
  disableDisplayNone,
}: Props) => {
  const contentElementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentElementRef.current != null) {
      const contentElement = contentElementRef.current;

      if (!isExpanded) {
        TweenLite.set(contentElement, { overflow: 'hidden' });
        TweenLite.to(contentElement, animationDurationSeconds || 0.25, {
          opacity: 0,
          height: 0,
          onComplete: () => {
            TweenLite.set(
              contentElement,
              disableDisplayNone ? { overflow: 'visible' } : { display: 'none', overflow: 'visible' },
            );
          },
        });
      } else {
        const elementHeight = window.getComputedStyle(contentElement).height || 0;
        const elementOpacity = window.getComputedStyle(contentElement).opacity || 0;

        TweenLite.set(contentElement, { height: 'auto', opacity: 1, display: 'block', overflow: 'hidden' });
        TweenLite.from(contentElement, animationDurationSeconds || 0.25, {
          height: elementHeight,
          opacity: elementOpacity,
          onComplete: () => {
            TweenLite.set(contentElement, { overflow: 'visible' });
          },
        });
      }
    }
  }, [isExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Content ref={contentElementRef} $height={isExpanded ? 'auto' : 0} className={className}>
      {children}
    </Content>
  );
};

const Content = styled.div<{ $height: string | number }>`
  overflow: hidden;
  height: ${props => props.$height};

  // Clearfix
  &:before,
  &:after {
    content: '.';
    display: block;
    height: 0;
    overflow: hidden;
  }

  &:after {
    clear: both;
  }

  zoom: 1;
`;
